import React from 'react';
import { motion } from 'framer-motion';
interface QuillWritingLoaderProps {
  phrase: string;
}
/**
 * Animated quill/feather writing on a parchment line.
 * The quill drifts horizontally as a gold underline grows beneath it,
 * simulating ink being laid down on a page.
 */
export const QuillWritingLoader = ({
  phrase
}: QuillWritingLoaderProps) => {
  return <motion.div className="fixed inset-0 z-40 flex flex-col items-center justify-center bg-[#0A0A0A] px-6" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4
  }} data-id="element-297">
      {/* Soft ambient glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(circle at 50% 45%, rgba(212,175,55,0.08) 0%, transparent 60%)'
    }} data-id="element-298" />

      {/* Quill writing scene */}
      <div className="relative w-72 md:w-96 mb-10" data-id="element-299">
        {/* Writing line (ink underline that draws in) */}
        <motion.div className="absolute left-0 right-0 h-px" style={{
        top: '52px',
        background: 'linear-gradient(90deg, transparent 0%, #D4AF37 15%, #D4AF37 85%, transparent 100%)',
        transformOrigin: 'left center'
      }} initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        duration: 2.4,
        ease: 'easeInOut'
      }} data-id="element-300" />

        {/* Feather / quill */}
        <motion.svg viewBox="0 0 100 100" className="absolute w-16 h-16 md:w-20 md:h-20" style={{
        top: 0
      }} initial={{
        x: 0
      }} animate={{
        x: [0, 220, 220]
      }} transition={{
        duration: 2.4,
        ease: 'easeInOut',
        times: [0, 0.9, 1]
      }} data-id="element-301">
          <defs data-id="element-302">
            <linearGradient id="featherGrad" x1="0" y1="0" x2="1" y2="1" data-id="element-303">
              <stop offset="0%" stopColor="#E5C97A" data-id="element-304" />
              <stop offset="50%" stopColor="#D4AF37" data-id="element-305" />
              <stop offset="100%" stopColor="#8a6e1f" data-id="element-306" />
            </linearGradient>
          </defs>

          {/* Feather plume */}
          <g transform="rotate(35 50 50)" data-id="element-307">
            <path d="M30 18 Q55 25 62 60 Q42 55 30 18 Z" fill="url(#featherGrad)" stroke="#D4AF37" strokeWidth="0.8" data-id="element-308" />
            {/* Plume barbs (lines suggesting feather texture) */}
            <path d="M34 25 L48 32" stroke="#8a6e1f" strokeWidth="0.5" opacity="0.7" data-id="element-309" />
            <path d="M34 32 L52 38" stroke="#8a6e1f" strokeWidth="0.5" opacity="0.7" data-id="element-310" />
            <path d="M34 39 L55 44" stroke="#8a6e1f" strokeWidth="0.5" opacity="0.7" data-id="element-311" />
            <path d="M34 46 L57 50" stroke="#8a6e1f" strokeWidth="0.5" opacity="0.7" data-id="element-312" />
            <path d="M34 53 L59 55" stroke="#8a6e1f" strokeWidth="0.5" opacity="0.7" data-id="element-313" />

            {/* Quill shaft */}
            <line x1="30" y1="18" x2="74" y2="78" stroke="#D4AF37" strokeWidth="1.2" strokeLinecap="round" data-id="element-314" />
            {/* Quill tip */}
            <path d="M72 75 L78 82 L74 80 Z" fill="#D4AF37" data-id="element-315" />
          </g>

          {/* Tiny ink dot trailing under the tip */}
          <motion.circle cx="68" cy="78" r="1.2" fill="#D4AF37" animate={{
          opacity: [0.3, 1, 0.3]
        }} transition={{
          duration: 0.6,
          repeat: Infinity
        }} data-id="element-316" />
        </motion.svg>
      </div>

      {/* Phrase */}
      <motion.p className="text-center max-w-xl font-cormorant italic" style={{
      color: '#D4AF37',
      fontSize: '17px',
      letterSpacing: '0.02em',
      lineHeight: 1.55
    }} initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} data-id="element-317">
        {phrase}
      </motion.p>

      {/* Tiny dots indicator */}
      <motion.div className="flex gap-1.5 mt-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.4
    }} data-id="element-318">
        {[0, 1, 2].map(i => <motion.span key={i} className="w-1.5 h-1.5 rounded-full bg-gold" animate={{
        opacity: [0.2, 1, 0.2]
      }} transition={{
        duration: 1.2,
        repeat: Infinity,
        delay: i * 0.2
      }} data-id="element-319" />)}
      </motion.div>
    </motion.div>;
};